import styled from 'styled-components'
import { useState, useRef, useEffect, RefObject } from 'react'
import { useTransition, animated } from '@react-spring/web'
import useScrollDirection from 'Hooks/useScrollDirection'

const Dimmer = ({
  targetRef,
  end = true,
}: {
  targetRef: RefObject<HTMLDivElement>
  end?: boolean
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false)
  const observer = useRef<IntersectionObserver | null>(null)
  const scrollDirection = useScrollDirection()

  const transitions = useTransition(isFullScreen, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  })

  // When the user scrolls to the canvas, set isFullScreen to true
  const handleIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setIsFullScreen(true)
      } else {
        setIsFullScreen(false)
      }
    })
  }

  useEffect(() => {
    if (!targetRef.current) return

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.9,
    }

    observer.current = new IntersectionObserver(handleIntersect, options)
    observer.current.observe(targetRef.current)

    return () => {
      if (observer.current && targetRef.current) {
        observer.current.unobserve(targetRef.current)
      }
    }
  }, [targetRef])

  return transitions((style, item) => item && <Container style={style} />)
}

const Container = styled(animated.div)`
  position: fixed;
  transition: background-color 1s ease;
  background-color: ${({ theme }) => `rgb(${theme.coloredBackground})`};
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
`

export default Dimmer
